import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import PageHeader from "@/components/PageHeader";
import Reveal from "@/components/Reveal";
import MaterialCard from "@/components/ProductCard";
import api from "@/lib/api";
import { Loader2 } from "lucide-react";

const FILTER_CATEGORIES = ["All", "Research Compounds", "Raw Cosmetic Grade", "Laboratory Supplies"];

export default function Materials() {
    const [searchParams] = useSearchParams();
    const [materials, setMaterials] = useState([]);
    const initial = searchParams.get("category");
    const [active, setActive] = useState(FILTER_CATEGORIES.includes(initial) ? initial : "All");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        api.get("/materials", { params: active === "All" ? {} : { category: active } })
            .then((res) => {
                if (!cancelled) setMaterials(res.data);
            })
            .catch(() => {
                if (!cancelled) setMaterials([]);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [active]);

    return (
        <div data-testid="materials-page">
            <PageHeader
                eyebrow="Catalogue"
                title="Research Materials"
                description="An informational catalogue of the research materials Grayhaven supplies — each backed by independent testing and documentation."
            />

            {/* Category filter */}
            <section className="px-6 lg:px-8 pb-24 sm:pb-32">
                <div className="max-w-7xl mx-auto">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-4xl" data-testid="category-filters">
                        {FILTER_CATEGORIES.map((category) => (
                            <button
                                key={category}
                                type="button"
                                onClick={() => setActive(category)}
                                data-testid={`category-filter-${category.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                                className={`px-6 py-4 text-center text-[0.65rem] uppercase tracking-[0.25em] font-semibold border transition-colors duration-300 ${
                                    active === category
                                        ? "bg-wine text-white border-wine"
                                        : "bg-white text-brand border-taupe/60 hover:border-wine/50 hover:text-wine"
                                }`}
                            >
                                {category}
                            </button>
                        ))}
                    </div>

                    {loading ? (
                        <div className="flex items-center gap-3 text-mist text-sm py-20">
                            <Loader2 className="h-4 w-4 animate-spin text-gold" /> Loading catalogue…
                        </div>
                    ) : materials.length === 0 ? (
                        <div className="mt-12 border border-dashed border-gold/30 rounded-sm py-20 px-8 text-center bg-white" data-testid="materials-empty">
                            <p className="font-serif text-2xl font-light text-brand">No materials in this category yet</p>
                            <p className="mt-4 text-sm leading-relaxed text-mist max-w-md mx-auto">
                                {active === "Laboratory Supplies"
                                    ? "Laboratory supplies will be added to the Grayhaven catalog soon."
                                    : "Materials will appear here once added to the catalog."}
                            </p>
                        </div>
                    ) : (
                        <div className="mt-12 grid grid-cols-2 gap-4 sm:gap-6 max-w-5xl" data-testid="materials-grid">
                            {materials.map((material, i) => (
                                <Reveal key={material.id} delay={Math.min(i * 0.06, 0.25)}>
                                    <MaterialCard material={material} />
                                </Reveal>
                            ))}
                        </div>
                    )}
                    <Reveal className="mt-10">
                        <p className="text-xs leading-relaxed text-slate-500 max-w-2xl">
                            Entries marked “Placeholder” are reserved for the Grayhaven inventory currently being
                            finalised — product names, sizes and pricing will be published once catalog data is
                            supplied. This catalog is built to support search, size and availability filtering as the
                            inventory is added.
                        </p>
                    </Reveal>
                </div>
            </section>
        </div>
    );
}
